import { Service } from 'constants/usercentrics';
import clsx from 'clsx';
import { useCallback, useContext, useEffect, useState } from 'react';
import Carousel from 'react-multi-carousel';
import Breadcrumbs from 'components/ui/Breadcrumbs';
import CustomSliderArrow from 'components/ui/CustomSliderArrow';
import { EmbedPlaceholder } from 'components/Shared/EmbedPlaceholder';
import TrustBox from 'components/ui/TrustBox';
import { findViewport } from 'utils/breakpoints';
import { throttle } from 'utils/throttle';
import { useTranslation } from 'hooks/useTranslation';
import { isServiceAllowed } from 'components/Common/CookieNotification';
import { CookieContext } from 'context/cookie';
import styles from './Hero.module.scss';

const videoIds = [
  'C-2S-q-mOSY',
  'S6ubJftHG64',
  'k1V5nlont1U',
  '8NOnMS3-0XQ',
  'I-pFv-WZSK4',
];

const sliderConfig = {
  classes: {
    showAfter: styles.showAfter,
    showBefore: styles.showBefore,
  },
  config: {
    responsive: {
      all: {
        breakpoint: { max: 4000, min: 0 },
        items: 2,
        partialVisibilityGutter: 60,
      },
    },
  },
};

type CustomDotProps = {
  onClick?: () => void;
  active?: boolean;
};

const CustomDot = ({ onClick, active }: CustomDotProps): JSX.Element => {
  return (
    <button
      className={clsx(
        active && styles.carouselDotsActive,
        !active && styles.carouselDotsInactive,
        styles.carouselDots,
      )}
      onClick={onClick}
    />
  );
};

const Hero = (): JSX.Element => {
  const { locale, t } = useTranslation();
  const { cookies } = useContext(CookieContext);

  const [videoId, setVideoId] = useState(videoIds[0]);
  const [curSlide, setCurSlide] = useState(0);
  const [isAllowed, setIsAllowed] = useState(false);
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const [isMediumScreen, setIsMediumScreen] = useState(false);

  const checkViewport = useCallback((newWidth: number): void => {
    const viewportName = findViewport(newWidth);
    if (['lg', 'xl'].includes(viewportName)) {
      setIsSmallScreen(false);
      setIsMediumScreen(false);
    }

    if ('md' === viewportName) {
      setIsMediumScreen(true);
      setIsSmallScreen(false);
    }

    if (['sm', 'xs'].includes(viewportName) || '' === viewportName) {
      setIsMediumScreen(false);
      setIsSmallScreen(true);
    }
  }, []);

  useEffect(() => {
    checkViewport(window.innerWidth);

    const throttledResizeListener = throttle(
      () => checkViewport(window.innerWidth),
      200,
    );

    window.addEventListener('resize', throttledResizeListener, {
      passive: true,
    });

    return () => {
      window.removeEventListener('resize', throttledResizeListener);
    };
  }, []);

  useEffect(() => {
    const serviceAllowed = isServiceAllowed(cookies, Service.YOUTUBE);

    setIsAllowed(serviceAllowed.serviceStatus);
  }, [cookies]);

  const breadcrumbs = [
    {
      link: t('reviews.slug'),
      title: t('reviews'),
    },
  ];

  const showVideoSubtitles = locale === 'de' ? 1 : 0;

  return (
    <div className="bg-navy">
      <Breadcrumbs
        className="md:pt-6 md:pb-8"
        crumbs={breadcrumbs}
        theme="light"
      />
      <div className="max-w-screen-xl mx-auto px-5 md:px-4 pb-8 lg:pt-12">
        <h1 className="text-tertiary text-4_3xl lg:hidden py-8">
          {t('whatOurPatientsAreSaying')}
        </h1>
        <div className="lg:flex justify-between">
          <div className="lg:order-2 xl:shrink-0 mx-1 md:mx-0">
            {!isAllowed ? (
              <EmbedPlaceholder service={Service.YOUTUBE} spacing="mb-6" />
            ) : (
              <>
                <iframe
                  src={`https://www.youtube-nocookie.com/embed/${videoId}?rel=0&cc_load_policy=${showVideoSubtitles}`}
                  allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full sm:mb-6 aspect-video"
                  style={{ boxShadow: '0px 2px 10px rgba(25, 1, 52, 0.12)' }}
                />
                {isSmallScreen ? (
                  <Carousel
                    responsive={sliderConfig.config.responsive}
                    arrows
                    partialVisible
                    customLeftArrow={
                      <CustomSliderArrow
                        direction="left"
                        className={styles.carouselArrowLeft}
                      />
                    }
                    customRightArrow={
                      <CustomSliderArrow
                        direction="right"
                        className={styles.carouselArrowRight}
                      />
                    }
                    customDot={<CustomDot />}
                    removeArrowOnDeviceType={['all']}
                    containerClass={clsx(
                      'flex relative items-center overflow-hidden h-40 pb-12',
                      styles.treatmentsCarousel,
                      curSlide > 0 &&
                        curSlide < videoIds.length - 2 && [
                          sliderConfig.classes.showBefore,
                          sliderConfig.classes.showAfter,
                        ],
                      curSlide === 0 && sliderConfig.classes.showAfter,
                      curSlide === videoIds.length - 2 &&
                        sliderConfig.classes.showBefore,
                    )}
                    sliderClass="flex relative flex-row list-none p-0 m-0"
                    itemClass={styles.carouselItem}
                    showDots
                    beforeChange={(nextSlide) => {
                      setCurSlide(nextSlide);
                    }}
                  >
                    {videoIds.map((id) => (
                      <div
                        key={id}
                        onClick={() => setVideoId(id)}
                        className="cursor-pointer rounded overflow-hidden mx-2 bg-navy"
                      >
                        <img
                          src={`https://img.youtube.com/vi/${id}/mqdefault.jpg`}
                          className="opacity-50 hover:opacity-100 transition duration-300 ease-in-out"
                          alt=""
                        />
                      </div>
                    ))}
                  </Carousel>
                ) : (
                  <div className="flex lg:-mx-2">
                    {videoIds.map((id) => (
                      <div
                        key={id}
                        onClick={() => setVideoId(id)}
                        className="cursor-pointer mx-2 rounded overflow-hidden bg-navy w-2/3"
                      >
                        <img
                          width={isMediumScreen ? '100%' : '132'}
                          src={`https://img.youtube.com/vi/${id}/mqdefault.jpg`}
                          className="opacity-50 hover:opacity-100 transition duration-300 ease-in-out"
                          alt=""
                        />
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
          <div className="text-white text-center lg:text-left">
            <div className="px-20 lg:pl-0 md:pr-12 xl:pr-48">
              <h1 className="text-tertiary text-4_3xl hidden lg:block leading-tight pt-0">
                {t('whatOurPatientsAreSaying')}
              </h1>
              <div className="lg:flex">
                <TrustBox theme="dark" className="mt-8 lg:mt-0 lg:w-52" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Hero;
